import { Capacitor } from '@capacitor/core';
import { SiteConfig, LatLong, EnergyMonitorType, TeslaState, TeslaConfig, VehicleState } from './db_types';
import { environment } from '../environments/environment';
import { GuiDistanceUnits } from './tesla_types';
import { DEFAULT_MAX_AMPS, DEFAULT_MIN_AMPS } from './api_types';


// determine the api url based on our context
export function getApiUrl(): string {
  // if it's configured then use that - used for prod/test builds
  if (environment.apiUrl) {
    return environment.apiUrl
  }
  if (Capacitor.getPlatform() == 'ios') {
    return 'http://localhost:4001' // the ios simulator can access localhost
  }
  if (Capacitor.getPlatform() == 'android') {
    return 'http://10.0.2.2:4001' // android emulator, maps through to localhost
  }
  if (window.location.hostname == 'localhost') {
    // when running as a webapp on localhost, we need to change the port
    return "http://localhost:4001"
  }
  else {
    // when running as a webapp from aws, the api is at the same path as the app
    // this path shouldn't generally be used, but could be handy
    return window.location.origin
  }
}

export function energyMonitorTypeDesc(type: EnergyMonitorType): string {
  switch (type) {
    case EnergyMonitorType.AlphaEss: return 'AlphaESS'
    case EnergyMonitorType.Enphase: return 'Enphase'
    case EnergyMonitorType.FroniusSolarweb: return 'Fronius'
    case EnergyMonitorType.Goodwe: return 'GoodWe'
    case EnergyMonitorType.Powerwall: return 'Tesla Solar / Powerwall'
    case EnergyMonitorType.PushApi: return 'Push API'
    case EnergyMonitorType.Sim: return 'Simulator'
    case EnergyMonitorType.SolarAnalytics: return 'Solar Analytics'
    case EnergyMonitorType.SolarEdgeApi: return 'SolarEdge'
    case EnergyMonitorType.SungrowISolarCloud: return 'Sungrow'
    default: return type
  }
}

export function hasControlDevice(siteConfig?: SiteConfig): boolean {
  return siteConfig?.tesla?.id_s != undefined || (siteConfig?.evses && siteConfig.evses.length > 0)
}

export function hasPowerControl(siteConfig?: SiteConfig): boolean {
  return siteConfig?.hasPowerControl == true
}

export function hasSoc(siteConfig?: SiteConfig): boolean {
  return siteConfig?.hasSoc == true
}
export function minValidChargeAmps(siteConfig?: SiteConfig): number {
  return siteConfig?.minValidChargeAmps ?? DEFAULT_MIN_AMPS
}

export function maxValidChargeAmps(siteConfig?: SiteConfig): number {
  return siteConfig?.maxValidChargeAmps ?? DEFAULT_MAX_AMPS
}

export function vehicleIsControlDevice(siteConfig?: SiteConfig): boolean {
  const hasEvse = siteConfig?.evses && siteConfig.evses.length > 0
  return siteConfig?.tesla?.id_s != undefined && !hasEvse
}

export function getMapsUrlLatLong(latLong: LatLong): string {
  return `https://www.google.com/maps/search/?api=1&query=${latLong.lat}%2C${latLong.long}`
}

export function nowTsms() {
  return Date.now()
}

export function secondsSinceTsms(tsms: number, now = Date.now()): number {
  return (now - tsms) / 1000
}

export function minutesSinceTsms(tsms: number, now = Date.now()): number {
  return (now - tsms) / 60000
}

export function hoursSinceTsms(tsms: number, now = Date.now()): number {
  return (now - tsms) / 3600000
}

export const KILOMETRES_PER_MILE = 1.609344
export const MILES_PER_KILOMETRE = 1 / KILOMETRES_PER_MILE

// return the range and the display unit, based on the tesla vehicle setting
export function buildTeslaRange(teslaState: TeslaState, teslaConfig: TeslaConfig): string | undefined {
  const rangeInMiles = teslaState.fields?.battery_range
  if (rangeInMiles != undefined) {
    const showMiles = teslaConfig.vehicleSettings?.gui_distance_units == GuiDistanceUnits.MiHr
    return showMiles ? `${Math.round(rangeInMiles)} mi`
      : `${Math.round(rangeInMiles * 1.609)} km`
  }
  return undefined
}

export function buildVehicleRange(vehicle: VehicleState, siteConfig: SiteConfig): string | undefined {
  if (vehicle.rangeKm != undefined) {
    // if tesla is showing miles then do the same, otherwise km
    // TODO: introduce our own app setting for this?
    const showMiles = siteConfig?.tesla?.vehicleSettings?.gui_distance_units == GuiDistanceUnits.MiHr
    return showMiles ? `${Math.round(vehicle.rangeKm * MILES_PER_KILOMETRE)} mi`
      : `${Math.round(vehicle.rangeKm)} km`
  }
  return undefined
}

// from https://javascript.plainenglish.io/deep-clone-an-object-and-preserve-its-type-with-typescript-d488c35e5574
export function deepCopy<T>(source: T): T {
  return Array.isArray(source)
    ? source.map(item => deepCopy(item))
    : source instanceof Date
      ? new Date(source.getTime())
      : source && typeof source === 'object'
        ? Object.getOwnPropertyNames(source).reduce((o, prop) => {
          Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop)!)
          o[prop] = deepCopy((source as { [key: string]: any })[prop])
          return o
        }, Object.create(Object.getPrototypeOf(source)))
        : source as T
}
